import { defineComponent as _defineComponent } from 'vue'
import Cleave from "vue-cleave-component";
  import ToggleButton from "@/components/ui/ToggleButton.vue";
  import { reactive, computed, onMounted, ref } from "vue";
  import useVuelidate from "@vuelidate/core";
  import {
    helpers,
    maxLength,
    minLength,
    required,
    minValue,
  } from "@vuelidate/validators";
  import router from "@/router";
  import { useStore } from "vuex";
  import { EstateInterface } from "@/types/EstateInterface";
  import { getFirstLastName } from "@/helpers/user";
  import { POSITION, useToast } from "vue-toastification";
  import {
    isValidFileSize,
    isValidImageType,
    validImagesTypes,
  } from "@/helpers/fileUpload";
  import { phoneNumberOption } from "@/helpers/cleavePlugin";
  import { latinCharactersRegex } from "@/helpers/regex";
  import getUserRole from "@/helpers/getUserRole";
  import { EstateAppVersion } from "@/enums/EstateAppVersion";
  import { EstateType } from "@/enums/EstateType";
  import { EstateAddon } from "@/enums/EstateAddon";
  import estateApi from "@/services/api/estateApi";
  import { CollaboratorCustomTerms, CustomTerms } from "@/types/CustomTerms";

  const tooltipText =
    "To connect a trust account and start accepting deposits for this project, please contact Customer Support.";
  
export default /*@__PURE__*/_defineComponent({
  __name: 'LandownerEstateUpdate',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();
  const toast = useToast();
  const estateId = router.currentRoute.value.params.estateId;
  const logoPreview = ref("");
  const file = ref<File | null>(null);
  const estate = ref<EstateInterface | null>();
  const loading = ref(true);
  const hlPackagesEnabled = ref(false);
  const { isSuperAdmin } = getUserRole();
  const isAdminEdit = computed(() => {
    return ["AdminEstateUpdate", "AdminEstateUpdateLawyer"].includes(
      router.currentRoute.value.name
    );
  });
  const isTrustAccountConnected = ref(false);
  const form = reactive<Partial<EstateInterface>>({
    title: "",
    shortTitle: "",
    address: "",
    isRefundable: true,
    iconColor: "#ffffff",
    initDeposit: 0,
    hidePrice: true,
    showInitialHold: false,
    showDocusign: false,
    appVersion: EstateAppVersion.V1,
    type: undefined,
    customTerms: {
      text: "",
      showOnlyCustomTerms: false,
    },
    collaboratorCustomTerms: [],
  });
  const initDepositOptions = {
    numeral: true,
    numeralThousandsGroupStyle: "thousand",
    prefix: "$",
    rawValueTrimPrefix: true,
  };
  const latinCharacters = helpers.regex(latinCharactersRegex);
  const rules = computed(() => {
    return {
      initDeposit: {
        required,
        minValue: minValue(getMinDepositValue.value),
      },
      shortTitle: {
        required,
        maxLength: maxLength(9),
        minLength: minLength(1),
      },
      title: {
        required,
        latinCharacters: helpers.withMessage("Invalid value", latinCharacters),
        $autoDirty: true,
      },
      address: { required, $autoDirty: true },
      appVersion: { required },
      type: { required },
    };
  });

  const getMinDepositValue = computed(() => {
    return initDepositCondition.value ? 1 : 0;
  });

  const initDepositCondition = ref(true);

  const v$ = useVuelidate(rules, form);
  const logoTypes = validImagesTypes.join(", ");

  onMounted(() => {
    store
      .dispatch("estate/getEstate", estateId)
      .then((res) => {
        estate.value = res.data;
        logoPreview.value = res.data.logoUrl;
        form.title = res.data.title;
        form.shortTitle = res.data.shortTitle;
        form.address = res.data.address;
        form.isRefundable = res.data.isRefundable;
        form.iconColor = res.data.iconColor;
        form.initDeposit = res.data.initDeposit / 100;
        form.hidePrice = res.data.hidePrice;
        isTrustAccountConnected.value = res.data.lawyerId;
        form.showInitialHold = res.data.showInitialHold;
        form.showDontHaveSolicitorEOI = res.data.showDontHaveSolicitorEOI;
        form.showDocusign = res.data.showDocusign;
        form.appVersion = res.data.appVersion || EstateAppVersion.V1;
        form.type = res.data.type;
        hlPackagesEnabled.value = res.data.addons?.includes(
          EstateAddon.HL_PACKAGES
        );
        form.customTerms = normalizeCustomTerms(res.data.customTerms);
        form.collaboratorCustomTerms = getCollaboratorCustomTerms(res.data);

        if (form.initDeposit > 0) {
          initDepositCondition.value = true;
        } else {
          initDepositCondition.value = false;
        }
      })
      .finally(() => {
        loading.value = false;
      });
  });

  function selectFile(e: any) {
    if (!e.target.files.length) {
      return;
    }

    const selectedFile = e.target.files[0];

    if (!isValidImageType(selectedFile) || !isValidFileSize(selectedFile)) {
      return;
    }

    file.value = selectedFile;
    const reader = new FileReader();

    reader.onload = (readerEvent: any) => {
      logoPreview.value = readerEvent.target.result;
    };
    reader.readAsDataURL(file.value as File);
  }
  const uploadFile = () => {
    const data = new FormData();

    data.append("file", file.value as File);
    return store.dispatch("estate/uploadEstateLogo", { estateId, file: data });
  };
  const updateEstate = async () => {
    v$.value.$touch();
    const isFormCorrect = await v$.value.$validate();

    if (!isFormCorrect) {
      return;
    }

    if (file.value) {
      await uploadFile();
    }

    const { initDeposit, ...props } = form;
    const normalizedForm = {
      initDeposit: initDeposit * 100,
      ...props,
    };

    store
      .dispatch("estate/updateEstate", { id: estateId, data: normalizedForm })
      .then(async () => {
        if (isSuperAdmin.value) {
          const currentAddons = estate.value?.addons ?? [];
          const shouldEnableHlPackages =
            form.type === EstateType.LAND_SUBDIVISION &&
            hlPackagesEnabled.value;
          const addons = shouldEnableHlPackages
            ? Array.from(new Set([...currentAddons, EstateAddon.HL_PACKAGES]))
            : currentAddons.filter(
                (addon) => addon !== EstateAddon.HL_PACKAGES
              );

          await estateApi.updateEstateAddons(estateId as string, addons);
        }

        if (!isAdminEdit.value) {
          router.push({
            name: "LandownerProfileEstates",
          });
        } else {
          router.push({
            name: "AdminEstate",
          });
        }
      })
      .catch((error) => {
        toast.error(error.response?.data?.message, {
          position: POSITION.TOP_LEFT,
        });
      });
  };

  const setDepositAndRefundable = () => {
    if (initDepositCondition.value) {
      form.isRefundable = true;
    } else {
      form.initDeposit = 0;
      form.isRefundable = false;
    }
  };
  const normalizeCustomTerms = (
    customTerms?: Partial<CustomTerms>
  ): CustomTerms => ({
    text: customTerms?.text || "",
    showOnlyCustomTerms: !!customTerms?.showOnlyCustomTerms,
  });

  const getCollaboratorCustomTerms = (
    data: EstateInterface
  ): CollaboratorCustomTerms[] => {
    const existingTerms = data.collaboratorCustomTerms || [];

    return (data.collaboratorBusiness || []).map((business) => {
      const existingItem = existingTerms.find(
        (item) => item.businessId === business._id
      );

      return {
        businessId: business._id,
        customTerms: normalizeCustomTerms(existingItem?.customTerms),
      };
    });
  };

  const getBusinessName = (businessId: string) => {
    return (
      estate.value?.collaboratorBusiness?.find(
        (business) => business._id === businessId
      )?.name || "Collaborator"
    );
  };

const __returned__ = { store, toast, estateId, logoPreview, file, estate, loading, hlPackagesEnabled, isSuperAdmin, isAdminEdit, isTrustAccountConnected, tooltipText, form, initDepositOptions, latinCharacters, rules, getMinDepositValue, initDepositCondition, v$, logoTypes, selectFile, uploadFile, updateEstate, setDepositAndRefundable, normalizeCustomTerms, getCollaboratorCustomTerms, getBusinessName, get Cleave() { return Cleave }, ToggleButton, reactive, computed, onMounted, ref, get useVuelidate() { return useVuelidate }, get helpers() { return helpers }, get maxLength() { return maxLength }, get minLength() { return minLength }, get required() { return required }, get minValue() { return minValue }, get router() { return router }, get useStore() { return useStore }, get EstateInterface() { return EstateInterface }, get getFirstLastName() { return getFirstLastName }, get POSITION() { return POSITION }, get useToast() { return useToast }, get isValidFileSize() { return isValidFileSize }, get isValidImageType() { return isValidImageType }, get validImagesTypes() { return validImagesTypes }, get phoneNumberOption() { return phoneNumberOption }, get latinCharactersRegex() { return latinCharactersRegex }, get getUserRole() { return getUserRole }, get EstateAppVersion() { return EstateAppVersion }, get EstateType() { return EstateType }, get EstateAddon() { return EstateAddon }, get estateApi() { return estateApi }, get CollaboratorCustomTerms() { return CollaboratorCustomTerms }, get CustomTerms() { return CustomTerms } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})